import ClearIcon from '@mui/icons-material/Clear';
import { Chip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { FC } from 'react';
import Box from '../../../Box';
import useMultiSelectYoda from './MultiSelectYoda.hook';
import { TagStyle } from './MultiSelectYoda.styles';
import { SelectOption } from 'basics/options/options.types';
import ArrowDownIcon from 'components/Icons/ArrowDownIcon';
import FormHelperText from 'yoda-ui/components/Form/FormHelperText';
import { MultiSelectYodaInputProps } from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda/MultiSelectYoda.types';
import { selectStyle, textFieldStyle } from 'yoda-ui/components/Form/SelectYoda/SelectYoda.styles';
import TextField from 'yoda-ui/components/Form/TextField';
import Paper from 'yoda-ui/components/Paper';
import { YodaSpacing } from 'yoda-ui/yodaTheme';

const MultiSelectYoda: FC<MultiSelectYodaInputProps> = ({
  defaultValue,
  disableClearable,
  disabled,
  freeOption = false,
  label,
  name,
  onChange,
  options,
  placeholder,
  required,
  validation,
  enableSelectAll = false,
  disableRenderTags = false,
  onSelectedChange,
  onDeleteOption,
  controlledValue,
}) => {
  const {
    fieldValue,
    fieldDefaultValue,
    fieldShowError,
    fieldErrorMessage,
    extendedOptions,
    findCurrentMultiOptionsByIds,
    handleAutocompleteChange,
  } = useMultiSelectYoda(
    name,
    defaultValue,
    onChange,
    validation,
    options,
    freeOption,
    enableSelectAll,
    onSelectedChange,
    onDeleteOption,
    controlledValue,
  );

  return (
    <Box>
      <Autocomplete
        data-testid="multi-select"
        multiple
        PaperComponent={
          ({ children }) => (
            <Paper margin={ YodaSpacing.small }>{ children }</Paper>
          )
        }
        popupIcon={ <ArrowDownIcon /> }
        sx={ selectStyle }
        freeSolo={ freeOption }
        disabled={ disabled }
        options={ extendedOptions }
        getOptionLabel={ (option) => option.label }
        defaultValue={ findCurrentMultiOptionsByIds(fieldDefaultValue) }
        disableClearable={ disableClearable }
        value={ findCurrentMultiOptionsByIds(fieldValue) }
        isOptionEqualToValue={ (option, value) => option.id === value.id }
        onChange={ handleAutocompleteChange }
        renderTags={
          disableRenderTags
            ? () => null
            : (value: readonly SelectOption[], getTagProps) => value.map((option: SelectOption, index: number) => (
              <Chip
                variant="filled"
                label={ option.label }
                { ...getTagProps({ index }) }
                key={ index }
                sx={ TagStyle }
                deleteIcon={ <ClearIcon /> }
              />
            ))
        }
        renderInput={
          (params) => (
            <TextField
              { ...params }
              sx={ textFieldStyle }
              label={ label }
              placeholder={ placeholder }
              required={ required }
              error={ fieldShowError }
            />
          )
        }
      />
      { fieldShowError && <FormHelperText error>{ fieldErrorMessage }</FormHelperText> }
    </Box>
  );
};

export default MultiSelectYoda;
